/**
 * The strip under the transcript's header: who is in this recording, and how much of it each of
 * them holds.
 *
 * **The label is the only rename affordance**, and it is the same shape whether a speaker has a
 * name or not — a name is never locked in. What changes between the two is only how loudly the
 * chip advertises itself: an unnamed one keeps its pencil visible because it marks work still to
 * be done, a named one shows it on hover. The pencil's slot is **reserved either way**, so nothing
 * on the strip moves when the pointer crosses it.
 *
 * The expand control at the right-hand end is the data it opens: a 160px bar of each speaker's
 * share of the talking, which is already useful closed (one glance says whether this was a
 * conversation or a monologue) and so needs no text label beside it.
 */

import type { TranscriptSpeakerNames } from "@repo/shared";
import { transcriptSpeakerDisplayLabel } from "@repo/shared";
import { ChevronDownIcon, ChevronUpIcon, PencilIcon } from "lucide-react";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { SpeakerActivity } from "../lib/speaker-activity";
import { speakerPaletteStyle } from "../lib/speaker-palette";

export type TranscriptSpeakerLegendProps = {
  /** Every speaker in the transcript, in the order they first speak. */
  speakers: readonly number[];
  speakerNames: TranscriptSpeakerNames;
  onRename: (speaker: number, name: string) => Promise<{ ok: boolean; message?: string }>;
  isRenaming: (speaker: number) => boolean;
  /** `null` when no turn carries a timestamp — there is then no timeline to offer. */
  activity: SpeakerActivity | null;
  isTimelineExpanded: boolean;
  onToggleTimeline: () => void;
};

export function TranscriptSpeakerLegend({
  speakers,
  speakerNames,
  onRename,
  isRenaming,
  activity,
  isTimelineExpanded,
  onToggleTimeline,
}: TranscriptSpeakerLegendProps) {
  const [editing, setEditing] = useState<number | null>(null);
  const [savedSpeaker, setSavedSpeaker] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (savedSpeaker === null) return;
    // The saved mark is a confirmation, not a state: it says the press landed and then gets out
    // of the way of the names it just changed.
    const timer = window.setTimeout(() => setSavedSpeaker(null), 1_400);
    return () => window.clearTimeout(timer);
  }, [savedSpeaker]);

  const namedCount = speakers.filter(
    (speaker) => (speakerNames[String(speaker)] ?? "").length > 0
  ).length;

  return (
    <div
      data-testid="transcript-speaker-legend"
      className="flex shrink-0 flex-wrap items-center gap-2 border-border border-b bg-surface-raised px-5 py-2 min-[840px]:px-6"
    >
      <span className="font-semibold text-[9.5px] text-muted-foreground uppercase tracking-[0.07em]">
        Speakers
      </span>
      {speakers.map((speaker) =>
        editing === speaker ? (
          <SpeakerNameEditor
            key={speaker}
            speaker={speaker}
            initialValue={speakerNames[String(speaker)] ?? ""}
            isSaving={isRenaming(speaker)}
            onCancel={() => {
              setEditing(null);
              setError(null);
            }}
            onSubmit={async (value) => {
              const result = await onRename(speaker, value);
              if (!result.ok) {
                setError(result.message ?? "Couldn't rename this speaker");
                return;
              }
              setError(null);
              setEditing(null);
              setSavedSpeaker(speaker);
            }}
          />
        ) : (
          <SpeakerChip
            key={speaker}
            speaker={speaker}
            speakerNames={speakerNames}
            isSaved={savedSpeaker === speaker}
            onEdit={() => {
              setError(null);
              setEditing(speaker);
            }}
          />
        )
      )}
      <span
        data-testid="transcript-speaker-count"
        className="flex-1 text-right text-[10.5px] text-muted-foreground"
      >
        {error ?? `${namedCount} of ${speakers.length} named`}
      </span>
      {activity === null ? null : (
        <button
          type="button"
          data-testid="transcript-timeline-toggle"
          aria-expanded={isTimelineExpanded}
          aria-label={isTimelineExpanded ? "Hide the activity timeline" : "Show who spoke when"}
          className="flex shrink-0 cursor-pointer items-center gap-2 focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2"
          onClick={onToggleTimeline}
        >
          <ShareBar activity={activity} />
          {isTimelineExpanded ? (
            <ChevronUpIcon className="size-3 text-muted-foreground" aria-hidden />
          ) : (
            <ChevronDownIcon className="size-3 text-muted-foreground" aria-hidden />
          )}
        </button>
      )}
    </div>
  );
}

/**
 * Each speaker's share of the talking, side by side in one 160px bar.
 *
 * Shares rather than positions: this is the *summary* the panel expands into detail, so it says
 * how much each voice spoke without claiming anything about when.
 */
function ShareBar({ activity }: { activity: SpeakerActivity }) {
  const total = activity.rows.reduce((sum, row) => sum + row.talkSeconds, 0);
  if (total <= 0) return null;
  return (
    <span data-testid="transcript-share-bar" className="flex h-2 w-[160px] overflow-hidden">
      {activity.rows.map((row) => (
        <span
          key={row.speaker ?? "unlabelled"}
          className="h-full bg-(--speaker-bar)"
          style={{
            ...speakerPaletteStyle(row.speaker ?? 1),
            width: `${(row.talkSeconds / total) * 100}%`,
          }}
        />
      ))}
    </span>
  );
}

function SpeakerChip({
  speaker,
  speakerNames,
  isSaved,
  onEdit,
}: {
  speaker: number;
  speakerNames: TranscriptSpeakerNames;
  isSaved: boolean;
  onEdit: () => void;
}) {
  const given = speakerNames[String(speaker)] ?? "";
  const isNamed = given.length > 0;
  return (
    <button
      type="button"
      data-testid="transcript-speaker-chip"
      data-speaker={speaker}
      data-named={isNamed}
      style={speakerPaletteStyle(speaker)}
      // The chip is a text target, not a button-shaped one: the pointer says "you can type here".
      className={cn(
        "group flex shrink-0 cursor-text items-center gap-1.5 bg-(--speaker-tint) px-1.5 py-0.5 font-medium text-[11px] text-(--speaker-ink) hover:bg-(--speaker-tint-hover)",
        "focus-visible:outline-2 focus-visible:outline-(--speaker-ink) focus-visible:outline-offset-1",
        isSaved && "ring-1 ring-(--speaker-ink)"
      )}
      aria-label={`Rename ${transcriptSpeakerDisplayLabel(speaker, speakerNames)}`}
      onClick={onEdit}
    >
      {transcriptSpeakerDisplayLabel(speaker, speakerNames)}
      {/* Always in the layout, so the strip does not shift when the pointer arrives. */}
      <PencilIcon
        data-testid="transcript-speaker-pencil"
        className={cn(
          "size-[9px] shrink-0 group-hover:opacity-100",
          isNamed ? "opacity-0" : "opacity-70"
        )}
        aria-hidden
      />
    </button>
  );
}

/**
 * The chip, swapped for an input of the same height in the same spot.
 *
 * Enter saves and Escape cancels — and **Escape stops there**: the dialog behind this closes on
 * Escape too, and cancelling an edit must not also close the transcript somebody is reading.
 */
function SpeakerNameEditor({
  speaker,
  initialValue,
  isSaving,
  onCancel,
  onSubmit,
}: {
  speaker: number;
  initialValue: string;
  isSaving: boolean;
  onCancel: () => void;
  onSubmit: (value: string) => Promise<void>;
}) {
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    inputRef.current?.select();
  }, []);

  return (
    <form
      data-testid="transcript-speaker-editor"
      data-speaker={speaker}
      className="flex shrink-0 items-center gap-1"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        void onSubmit(value);
      }}
    >
      <input
        ref={inputRef}
        data-testid="transcript-speaker-input"
        value={value}
        disabled={isSaving}
        // The blank submission is how a name is taken away again, so nothing here refuses it.
        placeholder={`Speaker ${speaker}`}
        aria-label={`Name for speaker ${speaker}`}
        className="h-[22px] w-[168px] border border-foreground bg-background px-1.5 text-[11.5px] outline-none"
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== "Escape") return;
          event.preventDefault();
          event.stopPropagation();
          onCancel();
        }}
      />
      <Button type="submit" size="sm" className="h-[22px] px-2 text-[10.5px]" disabled={isSaving}>
        Save
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-[22px] px-2 text-[10.5px]"
        onClick={onCancel}
      >
        Cancel
      </Button>
    </form>
  );
}
